<template>
    <div class="admin-vue-kit__panel">
        <div class="admin-vue-kit__toolbar">
            <template v-for="filter in definition.filters || []" :key="filter.key">
                <input
                    v-if="filter.type === 'search'"
                    v-model="filterValues[filter.key]"
                    class="admin-vue-kit__input admin-vue-kit__input--search"
                    type="search"
                    :placeholder="filter.placeholder || 'Cerca'"
                    :aria-label="filter.label || filter.placeholder || 'Cerca'"
                    @input="scheduleReload"
                >
                <select
                    v-else-if="filter.type === 'select'"
                    v-model="filterValues[filter.key]"
                    class="admin-vue-kit__input"
                    :aria-label="filter.label || filter.key"
                    @change="onFilterChange"
                >
                    <option v-for="opt in filter.options || []" :key="String(opt.value)" :value="opt.value">
                        {{ opt.label }}
                    </option>
                </select>
                <label v-else-if="filter.type === 'checkbox'" class="admin-vue-kit__checkbox">
                    <input
                        v-model="filterValues[filter.key]"
                        type="checkbox"
                        @change="onFilterChange"
                    >
                    {{ filter.label }}
                </label>
            </template>
            <button
                v-if="!filtersOnly && hasActiveFilters"
                type="button"
                class="admin-vue-kit__btn--outline"
                @click="resetFilters"
            >
                Reset filtri
            </button>
            <div v-if="!filtersOnly" class="admin-vue-kit__toolbar-spacer" />
            <button
                v-if="!filtersOnly && permissions.canMassDelete && selectedIds.length"
                type="button"
                class="admin-vue-kit__btn--danger"
                @click="$emit('mass-delete', selectedIds.slice())"
            >
                Elimina selezionati ({{ selectedIds.length }})
            </button>
            <button
                v-if="!filtersOnly && permissions.canExport && exportUrl"
                type="button"
                class="admin-vue-kit__btn--outline"
                :disabled="exporting"
                @click="runExport"
            >
                Export
            </button>
            <button
                v-if="!filtersOnly && permissions.canCreate"
                type="button"
                class="admin-vue-kit__btn--primary"
                @click="$emit('create')"
            >
                {{ createLabel }}
            </button>
        </div>

        <div v-if="loading && !rows.length" class="admin-vue-kit__loading">
            Caricamento…
        </div>
        <div
            v-else
            class="admin-vue-kit__table-wrap"
            :class="{ 'admin-vue-kit__table-wrap--scroll': filtersOnly }"
        >
            <table
                class="admin-vue-kit__table"
                :class="{ 'admin-vue-kit__table--nowrap': filtersOnly }"
                :aria-label="tableLabel"
            >
                <thead>
                    <tr>
                        <th v-if="showBulkColumn" scope="col" class="admin-vue-kit__bulk-col">
                            <input
                                type="checkbox"
                                :checked="allPageSelected"
                                :aria-label="allPageSelected ? 'Deseleziona pagina' : 'Seleziona pagina'"
                                @change="togglePageSelection"
                            >
                        </th>
                        <th
                            v-for="col in definition.columns || []"
                            :key="col.key"
                            scope="col"
                            :class="{ sortable: col.sortable }"
                            @click="col.sortable ? toggleSort(col.key) : null"
                        >
                            {{ col.label }}
                            <span v-if="col.sortable && sortKey === col.key">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th v-if="showActionsColumn" scope="col" class="actions-heading">Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in rows"
                        :key="row.id"
                        :class="{ 'is-deleted': isDeletedRow(row) }"
                    >
                        <td v-if="showBulkColumn">
                            <input
                                v-if="!isDeletedRow(row) || permissions.canForceDelete"
                                type="checkbox"
                                :checked="selectedIds.includes(row.id)"
                                :aria-label="`Seleziona riga ${row.id}`"
                                @change="toggleRowSelection(row.id)"
                            >
                        </td>
                        <td
                            v-for="col in definition.columns || []"
                            :key="col.key"
                            :class="columnCellClass(col)"
                        >
                            <slot :name="`cell-${col.key}`" :row="row" :value="row[col.key]">
                                <span
                                    v-if="col.key === 'status'"
                                    class="admin-vue-kit__status"
                                    :class="`admin-vue-kit__status--${row[col.key]}`"
                                >{{ row[col.key] }}</span>
                                <template v-else>{{ formatCell(row, col) }}</template>
                            </slot>
                        </td>
                        <td v-if="showActionsColumn" class="actions-cell">
                            <template v-if="isDeletedRow(row)">
                                <button
                                    v-if="permissions.canRestore"
                                    type="button"
                                    class="admin-vue-kit__row-cta admin-vue-kit__row-cta--primary"
                                    @click="$emit('restore', row)"
                                >
                                    Ripristina
                                </button>
                                <button
                                    v-if="permissions.canForceDelete"
                                    type="button"
                                    class="admin-vue-kit__row-cta admin-vue-kit__row-cta--danger"
                                    @click="$emit('force-delete', row)"
                                >
                                    Cancella definitivamente
                                </button>
                            </template>
                            <template v-else-if="filtersOnly && $slots['row-actions']">
                                <slot name="row-actions" :row="row" />
                            </template>
                            <template v-else>
                                <slot name="row-actions-before" :row="row" />
                                <a
                                    v-if="permissions.canDetail && routeFor('show', row.id)"
                                    class="admin-vue-kit__row-cta"
                                    :href="routeFor('show', row.id)"
                                >
                                    Dettagli
                                </a>
                                <button
                                    v-if="permissions.canEdit"
                                    type="button"
                                    class="admin-vue-kit__row-cta"
                                    @click="$emit('edit', row)"
                                >
                                    Modifica
                                </button>
                                <slot name="row-actions" :row="row" />
                                <button
                                    v-if="permissions.canDelete"
                                    type="button"
                                    class="admin-vue-kit__row-cta admin-vue-kit__row-cta--danger"
                                    @click="$emit('delete', row)"
                                >
                                    Elimina
                                </button>
                            </template>
                        </td>
                    </tr>
                    <tr v-if="!loading && rows.length === 0">
                        <td :colspan="emptyColspan" class="admin-vue-kit__empty">
                            {{ emptyMessage }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="admin-vue-kit__footer">
            <span>{{ footerRange }}</span>
            <div class="admin-vue-kit__pagination">
                <label class="admin-vue-kit__page-size">
                    Righe
                    <select
                        v-model.number="perPage"
                        class="admin-vue-kit__input admin-vue-kit__page-size-select"
                        aria-label="Righe per pagina"
                        @change="onPerPageChange"
                    >
                        <option v-for="n in pageSizeOptions" :key="n" :value="n">{{ n }}</option>
                    </select>
                </label>
                <button
                    type="button"
                    class="admin-vue-kit__btn--outline"
                    :disabled="page <= 1"
                    aria-label="Pagina precedente"
                    @click="goToPage(page - 1)"
                >
                    Precedente
                </button>
                <span class="admin-vue-kit__page-indicator">Pagina {{ page }} di {{ totalPages }}</span>
                <button
                    type="button"
                    class="admin-vue-kit__btn--outline"
                    :disabled="page >= totalPages"
                    aria-label="Pagina successiva"
                    @click="goToPage(page + 1)"
                >
                    Successiva
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import adminFetch from '../lib/adminFetch';
import adminAlert from '../lib/adminAlert';

export default {
    name: 'AdminDataTable',
    props: {
        definition: { type: Object, required: true },
        listingUrl: { type: String, required: true },
        permissions: {
            type: Object,
            default: () => ({}),
        },
        routes: {
            type: Object,
            default: () => ({}),
        },
        exportUrl: { type: String, default: '' },
        tableLabel: { type: String, default: 'Tabella' },
        createLabel: { type: String, default: 'Crea' },
        pageSizeOptions: {
            type: Array,
            default: () => [10, 25, 50, 100],
        },
        defaultPageSize: { type: Number, default: 25 },
        deletedKey: { type: String, default: 'deleted' },
        /** Toolbar shows filters only (no bulk/export/create). */
        filtersOnly: { type: Boolean, default: false },
        emptyMessage: { type: String, default: 'Nessun record.' },
        recordLabelSingular: { type: String, default: '' },
        recordLabelPlural: { type: String, default: '' },
        allScopeLabel: { type: String, default: '' },
        filteredScopeLabel: { type: String, default: 'Risultati filtrati' },
    },
    emits: ['create', 'edit', 'delete', 'restore', 'force-delete', 'mass-delete', 'loaded'],
    data() {
        const filterValues = {};
        (this.definition.filters || []).forEach((filter) => {
            if (filter.type === 'checkbox') {
                filterValues[filter.key] = Boolean(filter.default);
            } else {
                filterValues[filter.key] = filter.default ?? '';
            }
        });

        return {
            rows: [],
            total: 0,
            page: 1,
            perPage: this.defaultPageSize,
            sortKey: this.definition.defaultSort?.key || '',
            sortDir: this.definition.defaultSort?.direction || 'asc',
            filterValues,
            loading: false,
            exporting: false,
            selectedIds: [],
            reloadTimer: null,
        };
    },
    computed: {
        showBulkColumn() {
            return !this.filtersOnly && this.permissions.canMassDelete;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.total / this.perPage));
        },
        showActionsColumn() {
            const p = this.permissions;
            return p.canDetail || p.canEdit || p.canDelete || p.canRestore || p.canForceDelete
                || Boolean(this.$slots['row-actions'] || this.$slots['row-actions-before']);
        },
        emptyColspan() {
            let n = (this.definition.columns || []).length;
            if (this.showBulkColumn) {
                n += 1;
            }
            if (this.showActionsColumn) {
                n += 1;
            }

            return n;
        },
        paginationFrom() {
            if (this.total === 0) {
                return 0;
            }

            return (this.page - 1) * this.perPage + 1;
        },
        paginationTo() {
            return Math.min(this.page * this.perPage, this.total);
        },
        footerScope() {
            if (!this.allScopeLabel) {
                return '';
            }
            const search = String(this.filterValues.search ?? '').trim();
            const hasExtraFilter = (this.definition.filters || []).some((filter) => {
                if (filter.key === 'search') {
                    return false;
                }
                const value = this.filterValues[filter.key];
                if (filter.type === 'checkbox') {
                    return Boolean(value) !== Boolean(filter.default);
                }

                return String(value ?? '') !== String(filter.default ?? '');
            });
            if (search || hasExtraFilter) {
                return this.filteredScopeLabel;
            }

            return this.allScopeLabel;
        },
        footerRange() {
            if (this.total === 0) {
                if (this.recordLabelPlural) {
                    return `Nessuna ${this.recordLabelSingular || 'voce'}`;
                }

                return this.emptyMessage;
            }
            const from = this.paginationFrom;
            const to = this.paginationTo;
            const range = from === to ? `${from}` : `${from}-${to}`;
            if (this.recordLabelPlural) {
                const noun = this.total === 1 ? this.recordLabelSingular : this.recordLabelPlural;
                const scope = this.footerScope ? ` · ${this.footerScope}` : '';

                return `Mostrando ${range} di ${this.total} ${noun}${scope}`;
            }

            return `Mostrando ${range} di ${this.total}`;
        },
        allPageSelected() {
            if (!this.rows.length) {
                return false;
            }

            return this.rows.every((row) => this.selectedIds.includes(row.id));
        },
        hasActiveFilters() {
            return (this.definition.filters || []).some((filter) => {
                const value = this.filterValues[filter.key];
                if (filter.type === 'checkbox') {
                    return Boolean(value) !== Boolean(filter.default);
                }

                return String(value ?? '') !== String(filter.default ?? '');
            }) || Boolean(this.sortKey);
        },
    },
    mounted() {
        this.reload();
    },
    methods: {
        columnCellClass(col) {
            if (col.key === 'name') {
                return 'admin-vue-kit__cell--name';
            }
            if (col.key === 'slug') {
                return 'admin-vue-kit__cell--slug';
            }

            return undefined;
        },
        isDeletedRow(row) {
            return Boolean(row?.[this.deletedKey]);
        },
        routeFor(name, id) {
            const template = this.routes[name];
            if (!template) {
                return '';
            }

            return String(template).replace('__ID__', id);
        },
        formatCell(row, col) {
            const value = row[col.key];
            if (value === null || value === undefined || value === '') {
                return '—';
            }

            return value;
        },
        toggleSort(key) {
            if (this.sortKey === key) {
                this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortKey = key;
                this.sortDir = 'asc';
            }
            this.page = 1;
            this.reload();
        },
        scheduleReload() {
            clearTimeout(this.reloadTimer);
            this.reloadTimer = setTimeout(() => {
                this.page = 1;
                this.reload();
            }, 300);
        },
        onFilterChange() {
            this.page = 1;
            this.reload();
        },
        onPerPageChange() {
            this.page = 1;
            this.reload();
        },
        goToPage(next) {
            this.page = Math.min(Math.max(1, next), this.totalPages);
            this.reload();
        },
        resetFilters() {
            (this.definition.filters || []).forEach((filter) => {
                if (filter.type === 'checkbox') {
                    this.filterValues[filter.key] = Boolean(filter.default);
                } else {
                    this.filterValues[filter.key] = filter.default ?? '';
                }
            });
            this.sortKey = this.definition.defaultSort?.key || '';
            this.sortDir = this.definition.defaultSort?.direction || 'asc';
            this.page = 1;
            this.reload();
        },
        buildListingQuery() {
            const params = new URLSearchParams();
            params.set('page', String(this.page));
            params.set('per_page', String(this.perPage));
            if (this.sortKey) {
                params.set('sort', this.sortKey);
                params.set('direction', this.sortDir);
            }
            Object.entries(this.filterValues).forEach(([key, value]) => {
                if (value === '' || value === null || value === undefined) {
                    return;
                }
                if (typeof value === 'boolean') {
                    params.set(key, value ? '1' : '0');
                } else {
                    params.set(key, String(value));
                }
            });

            return params.toString();
        },
        async reload() {
            this.loading = true;
            try {
                const query = this.buildListingQuery();
                const url = query ? `${this.listingUrl}?${query}` : this.listingUrl;
                const data = await adminFetch(url);
                this.rows = Array.isArray(data.data) ? data.data : [];
                this.total = Number(data.meta?.total ?? this.rows.length);
                this.page = Number(data.meta?.page ?? this.page);
                this.$emit('loaded', { total: this.total, rows: this.rows });
            } catch (error) {
                await adminAlert(error.message, 'error');
                this.rows = [];
                this.total = 0;
            } finally {
                this.loading = false;
            }
        },
        toggleRowSelection(id) {
            if (this.selectedIds.includes(id)) {
                this.selectedIds = this.selectedIds.filter((value) => value !== id);
            } else {
                this.selectedIds = [...this.selectedIds, id];
            }
        },
        togglePageSelection(event) {
            if (event.target.checked) {
                const ids = this.rows.map((row) => row.id);
                this.selectedIds = [...new Set([...this.selectedIds, ...ids])];
            } else {
                const pageIds = new Set(this.rows.map((row) => row.id));
                this.selectedIds = this.selectedIds.filter((id) => !pageIds.has(id));
            }
        },
        clearSelection() {
            this.selectedIds = [];
        },
        async runExport() {
            if (!this.exportUrl || this.exporting) {
                return;
            }
            this.exporting = true;
            try {
                const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const body = new FormData();
                body.append('_token', token);
                body.append('export', '1');
                Object.entries(this.filterValues).forEach(([key, value]) => {
                    if (value !== '' && value !== null && value !== undefined) {
                        body.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
                    }
                });
                const response = await fetch(this.exportUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': token,
                    },
                    credentials: 'same-origin',
                    body,
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || 'Export non riuscito.');
                }
                await adminAlert(data.message || 'Export avviato in background.');
            } catch (error) {
                await adminAlert(error.message, 'error');
            } finally {
                this.exporting = false;
            }
        },
    },
};
</script>
